import {
  useFetchEventQuery,
  useFetchUtilsEventQuery,
} from 'features/private-views/event/eventApiSlice'
import { Calendar, ChevronRight } from 'react-feather'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Card, CardBody, Spinner } from 'reactstrap'
import { COLOR_MAP } from 'config/templateConfig'
import { formatDateOnlyPMB } from 'utils/formatDate'
import { URL_EVENT } from 'utils/pathUrl'

const SectionEventWidget = ({ mode = 'secondary', params = {} }) => {
  const navigate = useNavigate()
  const profile = useOutletContext()

  const userId =
    profile?.data?.makerData?.userId || profile?.data?.ownerData?.userId

  const { data: eventData, isLoading } = useFetchEventQuery({
    page: 0,
    size: 1,
    userId: userId || undefined,
  })

  const { data: utilsData } = useFetchUtilsEventQuery()

  const items = eventData?.items || []
  const latestEvent = items.length > 0 ? items[0] : null

  const getDynamicBadgeColor = (list, value) => {
    const lowerValue = value?.toLowerCase?.() || ''
    const index = list?.findIndex(
      (item) => item.label.toLowerCase() === lowerValue,
    )

    if (index === -1) return 'bg-light text-secondary'

    const colors = [
      'bg-primary-50 text-primary',
      'bg-success-50 text-success',
      'bg-warning-50 text-warning-700',
      'bg-info-50 text-info',
      'bg-danger-50 text-danger-700',
    ]

    return colors[index % colors.length]
  }

  const badgeColor = getDynamicBadgeColor(
    utilsData?.eventType || [],
    latestEvent?.eventTypeName,
  )

  const handleRoute = () => {
    navigate(URL_EVENT)
  }

  // Mode Styling Configuration matching GenericLinkWidget
  let paddingClass = 'p-4'
  let titleClass = 'fw-bold mb-1'
  let iconSize = 24
  let iconPaddingClass = 'p-2 me-3'
  let descClass = 'mb-0'

  let isTinySquare = mode === 'tiny-square'
  let isTiny = mode === 'tiny' || isTinySquare

  if (mode === 'primary') {
    paddingClass = 'p-4'
    titleClass = 'fw-bold mb-1'
    iconSize = 36
    iconPaddingClass = 'p-3 me-3'
    descClass = 'mb-0'
  } else if (mode === 'compact') {
    paddingClass = 'p-3'
    titleClass = 'fw-bold mb-1'
    iconSize = 18
    iconPaddingClass = 'p-2 me-2'
    descClass = 'mb-0'
  } else if (isTiny) {
    paddingClass = isTinySquare ? 'p-2' : 'p-2 px-3'
    titleClass = `fw-bold mb-0 ${isTinySquare ? 'text-center' : 'text-start'}`
    iconSize = 16
    iconPaddingClass = 'p-2'
  }

  // Determine accent colors based on Event type
  const cardColor = COLOR_MAP[params.color] || '#B25EE2'

  return (
    <div className={!isTiny ? 'h-100' : ''}>
      <Card
        onClick={handleRoute}
        className={`${!isTiny ? 'h-100' : ''} border-0 shadow-sm transition-all`}
        style={{
          background: cardColor,
          backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.05) 100%)',
          color: '#ffffff',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          aspectRatio: isTinySquare ? '1/1' : undefined,
          width: '100%',
          minHeight: isTinySquare ? undefined : mode === 'tiny' ? '68px' : mode === 'primary' ? '236px' : mode === 'compact' ? '110px' : '152px',
          borderRadius: isTiny ? '12px' : '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isTinySquare ? 'center' : 'flex-start',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px)'
          e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        <CardBody className={`${paddingClass} ${isTiny ? '' : 'flex-grow-1'} d-flex flex-column justify-content-center ${isTinySquare ? 'align-items-center justify-content-center text-center' : ''}`}>
          <div className={isTinySquare ? 'd-flex flex-column align-items-center justify-content-center mb-0 w-100' : `d-flex align-items-center w-100 ${mode === 'primary' ? 'mb-2' : 'mb-0'}`}>
            <div
              className={`${iconPaddingClass} ${isTinySquare ? 'mb-2' : 'me-3'} rounded-circle d-flex align-items-center justify-content-center`}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                border: 'none',
                flexShrink: 0,
                width: mode === 'primary' ? '64px' : (mode === 'compact' || isTiny) ? '36px' : '48px',
                height: mode === 'primary' ? '64px' : (mode === 'compact' || isTiny) ? '36px' : '48px',
                boxShadow: 'inset 0 0 10px rgba(255,255,255,0.1)'
              }}
            >
              <Calendar size={iconSize} />
            </div>
            
            <div className={isTinySquare ? 'w-100' : 'flex-grow-1 d-flex flex-column justify-content-center'}>
              <div
                className={`${titleClass} ${isTiny ? 'd-block text-truncate' : 'text-truncate'}`}
                style={{ 
                  color: '#ffffff', 
                  fontSize: isTiny ? '14px' : mode === 'primary' ? '28px' : mode === 'compact' ? '15px' : '18px',
                  fontWeight: 600,
                  marginBottom: !isTiny ? '2px' : '0',
                  lineHeight: 1.1
                }}
              >
                Event
              </div>
              
              {!isTiny && (
                <div className="small d-flex align-items-center" style={{ color: 'rgba(255,255,255,0.7)', fontSize: mode === 'compact' ? '11px' : '12px', marginTop: mode === 'primary' ? '4px' : '2px' }}>
                  <ChevronRight size={10} className="me-1" />
                  <span>Lihat Detail Event</span>
                </div>
              )}
              {isTiny && latestEvent && (
                <div className="text-white-50 text-truncate" style={{ fontSize: 13, maxWidth: '120px', alignSelf: isTinySquare ? 'center' : 'flex-start' }}>
                  {latestEvent.title}
                </div>
              )}
            </div>
          </div>

          {!isTiny && (
            <div className="mt-2" style={{ color: 'rgba(255,255,255,0.7)', fontSize: mode === 'compact' ? '12px' : '14px', lineHeight: '1.4' }}>
              {isLoading ? (
                <div className="d-flex mt-1">
                  <Spinner size="sm" color="light" />
                </div>
              ) : latestEvent ? (
                <div className={`d-flex flex-column gap-1 ${mode==='compact'?'mt-0':'mt-1'}`}>
                  <div className="d-flex align-items-center gap-2 flex-nowrap">
                    <span
                       className="text-white fw-semibold text-truncate"
                       style={{
                         fontSize: mode === 'compact' ? 13 : 15,
                         maxWidth: mode === 'compact' ? '140px' : '200px',
                         display: 'inline-block',
                       }}
                    >
                      {latestEvent.title}
                    </span>
                    <span
                      className={`badge rounded-pill px-2 bg-light text-dark`}
                      style={{ fontSize: mode === 'compact' ? 9 : 11, whiteSpace: 'nowrap', opacity: 0.9 }}
                    >
                      {latestEvent.eventTypeName || 'Event'}
                    </span>
                  </div>
                  <span className="fw-medium" style={{ color: 'rgba(255,255,255,0.8)', fontSize: mode === 'compact' ? 12 : 14 }}>
                     {formatDateOnlyPMB(latestEvent.eventDate ?? latestEvent?.startDate)}
                  </span>
                </div>
              ) : (
                <div className={`d-flex flex-column gap-1 ${mode==='compact'?'mt-0':'mt-1'}`}>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: mode === 'compact' ? '12px' : '14px' }}>
                    Belum ada event
                  </span>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default SectionEventWidget
